import { useState } from "react";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ExternalLink, AlertTriangle } from "lucide-react";
import { Vulnerability } from "@/types/vulnerability";
import { AppleDeviceIcon } from "@/components/icons";
import { DataTable } from "@/components/ui/data-table";

interface VulnerabilityTableProps {
  vulnerabilities: Vulnerability[];
  onRowClick: (vulnerability: Vulnerability) => void;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-destructive text-destructive-foreground hover:bg-destructive/90';
    case 'HIGH':
      return 'bg-[hsl(30_80%_55%)] text-white hover:bg-[hsl(30_80%_45%)]';
    case 'MEDIUM':
      return 'bg-[hsl(45_90%_55%)] text-black hover:bg-[hsl(45_90%_45%)]';
    case 'LOW':
      return 'bg-[hsl(220_70%_50%)] text-white hover:bg-[hsl(220_70%_40%)]';
    default:
      return 'bg-muted text-muted-foreground hover:bg-muted/80';
  }
};

const getSeverityProgress = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return 100;
    case 'HIGH':
      return 75;
    case 'MEDIUM':
      return 50;
    case 'LOW':
      return 25;
    default:
      return 0;
  }
};

const extractPlatforms = (softwareName: string): string[] => {
  const platforms: string[] = [];
  const name = softwareName.toLowerCase();
  
  // Detect iPadOS first (more specific)
  if (name.includes('ipados')) platforms.push('iPadOS');

  // Detect iOS as a standalone token (avoid matching "visionOS")
  if (/\bios\b|\bios\s|\bios\d/.test(name)) platforms.push('iOS');
  
  if (name.includes('macos') || name.includes('mac os')) platforms.push('macOS');
  if (name.includes('watchos')) platforms.push('watchOS');
  if (name.includes('tvos')) platforms.push('tvOS');
  if (name.includes('visionos')) platforms.push('visionOS');
  
  return platforms;
};

// Map platform to branded OS icon variant for consistent platform logos
const getPlatformVariant = (platform: string) => {
  switch (platform) {
    case 'iOS':
      return 'ios-branded';
    case 'iPadOS':
      return 'ipados-branded';
    case 'macOS':
      return 'macos-branded';
    case 'watchOS':
      return 'watchos-branded';
    default:
      return undefined;
  }
};

export function VulnerabilityTable({ vulnerabilities, onRowClick }: VulnerabilityTableProps) {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({
    cve_id: 180,
    software_names: 250,
    severity: 150,
    cvss_score: 120,
    actively_exploited: 140,
    affected_updates: 140,
  });

  const columns: ColumnDef<Vulnerability>[] = [
    {
      id: 'cve_id',
      accessorKey: 'cve_id',
      header: 'Vulnerability ID',
      size: 180,
      cell: ({ row }) => (
        <a
          href={`https://nvd.nist.gov/vuln/detail/${row.original.cve_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 font-mono text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          {row.original.cve_id}
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
    },
    {
      id: 'software_names',
      accessorKey: 'software_names',
      header: 'Vulnerable Software',
      size: 250,
      cell: ({ row }) => {
        const vuln = row.original;
        return (
          <div className="flex flex-wrap gap-2">
            {vuln.software_names.slice(0, 2).map((name, idx) => {
              const detectedPlatforms = extractPlatforms(name);
              const fallback = vuln.os_types[idx] || 'macOS';
              const primaryPlatform = detectedPlatforms.includes('iOS')
                ? 'iOS'
                : (detectedPlatforms[0] || fallback);
              
              return (
                <div key={idx} className="flex items-center gap-1">
                  <AppleDeviceIcon 
                    osType={primaryPlatform} 
                    variant={getPlatformVariant(primaryPlatform)}
                    className="h-4 w-4" 
                  />
                  <span className="text-sm">{name}</span>
                </div>
              );
            })}
            {vuln.software_names.length > 2 && (
              <Badge variant="secondary" className="text-xs">
                +{vuln.software_names.length - 2} more
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: 'severity',
      accessorKey: 'severity',
      header: 'Severity',
      size: 150,
      cell: ({ row }) => {
        const severity = row.original.severity;
        return (
          <div className="space-y-2">
            <Badge className={getSeverityColor(severity)}>
              {severity}
            </Badge>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${getSeverityProgress(severity)}%`,
                  backgroundColor: severity === 'CRITICAL' ? 'hsl(0 84.2% 60.2%)' :
                    severity === 'HIGH' ? 'hsl(30 80% 55%)' :
                    severity === 'MEDIUM' ? 'hsl(45 90% 55%)' :
                    'hsl(220 70% 50%)'
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: 'cvss_score',
      accessorKey: 'cvss_score',
      header: () => <div className="text-right">CVSS Score</div>,
      size: 120,
      cell: ({ row }) => (
        <div className="text-right">
          <span className="font-semibold">{row.original.cvss_score.toFixed(1)}</span>
          <span className="text-xs text-muted-foreground ml-1">/10</span>
        </div>
      ),
    },
    {
      id: 'actively_exploited',
      accessorKey: 'actively_exploited',
      header: () => <div className="text-center">Known Exploit</div>,
      size: 140,
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.actively_exploited ? (
            <div className="flex items-center justify-center gap-1">
              <AlertTriangle className="h-4 w-4 text-[hsl(30_80%_55%)]" />
              <span className="text-sm font-semibold text-[hsl(30_80%_55%)]">Yes</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No</span>
          )}
        </div>
      ),
    },
    {
      id: 'affected_updates',
      accessorKey: 'affected_updates',
      header: () => <div className="text-right">Affected Updates</div>,
      size: 140,
      cell: ({ row }) => (
        <div className="text-right">
          <span className="font-semibold">{row.original.affected_updates.length}</span>
        </div>
      ),
    },
  ];

  if (vulnerabilities.length === 0) {
    return (
      <div className="text-center py-12 backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg">
        <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No vulnerabilities found matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg overflow-hidden">
      <DataTable
        data={vulnerabilities}
        columns={columns}
        enableColumnResizing={true}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        onRowClick={onRowClick}
      />
    </div>
  );
}
